/**
 * 管理员用户详情与编辑
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { AdminUserDetail as AdminUserDetailType } from '../../types';
import { confirm } from '../../store/toastStore';

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [subscriptionExpiry, setSubscriptionExpiry] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!id) return;
    adminApi
      .getUser(parseInt(id))
      .then((res) => {
        const data = res.data.data;
        setUser(data);
        setSubscriptionExpiry(
          data.subscription_expiry
            ? new Date(data.subscription_expiry).toISOString().slice(0, 10)
            : ''
        );
      })
      .catch(() => setError('加载用户信息失败'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleResetPassword = async () => {
    if (!newPassword || !id) return;
    const ok = await confirm({ title: '重置密码', message: `确定要重置用户 ${user?.username} 的密码吗？` });
    if (!ok) return;
    try {
      await adminApi.updateUser(parseInt(id), { new_password: newPassword });
      setSuccessMsg('密码已重置');
      setNewPassword('');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || '操作失败');
    }
  };

  const handleToggleActive = async () => {
    if (!id || !user) return;
    const newActive = !user.is_active;
    const ok = await confirm({
      title: newActive ? '启用账号' : '禁用账号',
      message: `确定要${newActive ? '启用' : '禁用'}用户 ${user.username} 吗？`,
      danger: !newActive,
    });
    if (!ok) return;
    try {
      await adminApi.updateUser(parseInt(id), { is_active: newActive });
      setUser({ ...user, is_active: newActive });
      setSuccessMsg(newActive ? '账号已启用' : '账号已禁用');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || '操作失败');
    }
  };

  const handleSaveSubscription = async () => {
    if (!id) return;
    const expiry = subscriptionExpiry ? new Date(subscriptionExpiry).toISOString() : null;
    const label = expiry ? `设置到期时间为 ${new Date(expiry).toLocaleString('zh-CN')}` : '撤销权益';
    const ok = await confirm({ title: '修改权益', message: `确定要${label}吗？` });
    if (!ok) return;
    try {
      await adminApi.updateSubscription(parseInt(id), { subscription_expiry: expiry });
      setUser((prev) => (prev ? { ...prev, subscription_expiry: expiry } : null));
      setSuccessMsg('权益已更新');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || '操作失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">加载中...</p>
      </div>
    );
  }

  if (!user) {
    return <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">用户不存在</div>;
  }

  return (
    <div>
      <button onClick={() => navigate('/admin/users')} className="text-sm text-primary hover:text-primary mb-4 inline-block">
        &larr; 返回用户列表
      </button>
      <h2 className="text-xl font-bold text-foreground mb-6">用户详情</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">{successMsg}</div>
      )}

      {/* 用户信息 */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">基本信息</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-muted-foreground">用户名：</span>{user.username}</div>
          <div><span className="text-muted-foreground">邮箱：</span>{user.email}</div>
          <div><span className="text-muted-foreground">全名：</span>{user.full_name || '-'}</div>
          <div>
            <span className="text-muted-foreground">角色：</span>
            {user.role === 'admin' ? (
              <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded">管理员</span>
            ) : (
              '用户'
            )}
          </div>
          <div><span className="text-muted-foreground">注册时间：</span>{new Date(user.created_at).toLocaleString('zh-CN')}</div>
          <div><span className="text-muted-foreground">项目数量：</span>{user.project_count}</div>
          <div>
            <span className="text-muted-foreground">订阅到期：</span>
            {user.subscription_expiry
              ? new Date(user.subscription_expiry).toLocaleString('zh-CN')
              : '从未付费'}
          </div>
          <div>
            <span className="text-muted-foreground">状态：</span>
            {user.is_active ? (
              <span className="text-green-600">正常</span>
            ) : (
              <span className="text-red-600">已禁用</span>
            )}
          </div>
        </div>
      </div>

      {/* 仅对普通用户显示操作区 */}
      {!isAdmin && (
        <>
          {/* 重置密码 */}
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">重置密码</h3>
            <div className="flex gap-3">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="输入新密码（至少6位）"
                className="px-3 py-2 border border-border rounded-md text-sm w-64"
              />
              <button
                onClick={handleResetPassword}
                disabled={!newPassword}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                重置密码
              </button>
            </div>
          </div>

          {/* 启用/禁用 */}
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">账号状态</h3>
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 text-sm text-white rounded-md ${
                user.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {user.is_active ? '禁用账号' : '启用账号'}
            </button>
          </div>

          {/* 权益管理 */}
          <div className="bg-card rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">权益管理</h3>
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="date"
                value={subscriptionExpiry}
                onChange={(e) => setSubscriptionExpiry(e.target.value)}
                className="px-3 py-2 border border-border rounded-md text-sm"
              />
              <button
                onClick={handleSaveSubscription}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                保存
              </button>
              {user.subscription_expiry && (
                <button
                  onClick={async () => {
                    setSubscriptionExpiry('');
                    const ok = await confirm({ title: '撤销权益', message: `确定要撤销用户 ${user.username} 的权益吗？`, danger: true });
                    if (!ok) return;
                    try {
                      await adminApi.updateSubscription(parseInt(id!), { subscription_expiry: null });
                      setUser({ ...user, subscription_expiry: null });
                      setSuccessMsg('权益已撤销');
                      setTimeout(() => setSuccessMsg(''), 3000);
                    } catch (err: any) {
                      setError(err.response?.data?.detail || '操作失败');
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                >
                  撤销权益
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {isAdmin && (
        <div className="bg-card rounded-lg shadow p-6">
          <p className="text-muted-foreground text-sm">不能修改其他管理员的账号。</p>
        </div>
      )}

    </div>
  );
};

export default AdminUserDetail;
